import React from 'react';
import styled from '@emotion/native';
import useExploreStore from '@/stores/useExploreStore';
import {
  AgeRange,
  Gender,
  PricingTier,
  SessionType,
  City,
} from '@/shared/types/generated';
import { Expertise, StylingPreference } from '@/shared/types/stylist';
import FilterPill from './FilterPill';
import { ScrollView, useWindowDimensions } from 'react-native';
import { BaseText } from '@/shared/components/StyledText';
import { SCREEN_PADDING } from '@/components/bookConsultation/BookingSummary/constants';
import { PRICE_TIERS } from './PriceFilters';
import { getCityLabel } from './utils';

const FilterPills: React.FC = () => {
  const { filters, setFilters } = useExploreStore();

  const handleRemovePriceTier = (tier: PricingTier) => {
    setFilters({
      priceTier: filters.priceTier.filter((t) => t !== tier),
    });
  };

  const handleRemoveExpertise = (expertise: Expertise) => {
    setFilters({
      expertise: filters.expertise.filter((e) => e !== expertise),
    });
  };

  const handleRemoveStylingPreference = (preference: StylingPreference) => {
    setFilters({
      stylingPreferences: filters.stylingPreferences.filter(
        (p) => p !== preference,
      ),
    });
  };

  const handleRemoveAge = (age: AgeRange) => {
    setFilters({
      age: filters.age.filter((a) => a !== age),
    });
  };

  const handleRemoveGender = () => {
    setFilters({ gender: null });
  };

  const handleRemoveSessionType = () => {
    setFilters({ sessionType: null });
  };

  const handleRemoveInPersonAvailability = () => {
    setFilters({ availableInPerson: null });
  };

  const handleRemoveCity = (city: City) => {
    setFilters({
      cities: filters.cities.filter((c) => c !== city),
    });
  };

  const getPriceTierLabel = (tier: PricingTier) => {
    const priceTier = PRICE_TIERS.find((pt) => pt.tier === tier);
    return priceTier ? priceTier.title : '';
  };

  const getAgeLabel = (age: AgeRange) => {
    switch (age) {
      case AgeRange.Age_18_24:
        return '18-24';
      case AgeRange.Age_25_34:
        return '25-34';
      case AgeRange.Age_35_44:
        return '35-44';
      case AgeRange.Age_45_54:
        return '45-54';
      case AgeRange.Age_55_64:
        return '55-64';
      case AgeRange.Age_65Plus:
        return '65+';
      default:
        return '';
    }
  };

  const getGenderLabel = (gender: Gender) => {
    switch (gender) {
      case Gender.Female:
        return 'FEMALE';
      case Gender.Male:
        return 'MALE';
      case Gender.NonBinary:
        return 'NON-BINARY';
      default:
        return '';
    }
  };

  const getSessionTypeLabel = (sessionType: SessionType) => {
    switch (sessionType) {
      case SessionType.Video:
        return 'Video Sessions';
      case SessionType.InPersonEvent:
        return 'In-Person at Event';
      default:
        return '';
    }
  };

  const width = useWindowDimensions().width;

  const hasActiveFilters =
    filters.priceTier.length > 0 ||
    filters.expertise.length > 0 ||
    filters.stylingPreferences.length > 0 ||
    filters.age.length > 0 ||
    filters.gender ||
    filters.sessionType !== null ||
    filters.availableInPerson === true ||
    filters.cities.length > 0;

  return (
    <Container width={width}>
      {hasActiveFilters && (
        <BaseText style={{ marginRight: 8 }}>Filtered by:</BaseText>
      )}
      <ScrollContainer horizontal showsHorizontalScrollIndicator={false}>
        {filters.priceTier.map((tier) => (
          <FilterPill
            key={tier}
            label={getPriceTierLabel(tier)}
            onRemove={() => handleRemovePriceTier(tier)}
          />
        ))}
        {filters.expertise.map((expertise) => (
          <FilterPill
            key={expertise}
            label={expertise}
            onRemove={() => handleRemoveExpertise(expertise)}
          />
        ))}
        {filters.stylingPreferences.map((preference) => (
          <FilterPill
            key={preference}
            label={preference}
            onRemove={() => handleRemoveStylingPreference(preference)}
          />
        ))}
        {filters.age.map((age) => (
          <FilterPill
            key={age}
            label={`Age: ${getAgeLabel(age)}`}
            onRemove={() => handleRemoveAge(age)}
          />
        ))}
        {filters.gender && (
          <FilterPill
            label={getGenderLabel(filters.gender)}
            onRemove={handleRemoveGender}
          />
        )}
        {filters.sessionType && (
          <FilterPill
            label={getSessionTypeLabel(filters.sessionType)}
            onRemove={handleRemoveSessionType}
          />
        )}
        {filters.availableInPerson === true && (
          <FilterPill
            label="Available In Person"
            onRemove={handleRemoveInPersonAvailability}
          />
        )}
        {filters.cities.map((city) => (
          <FilterPill
            key={city}
            label={getCityLabel(city)}
            onRemove={() => handleRemoveCity(city)}
          />
        ))}
      </ScrollContainer>
    </Container>
  );
};

const Container = styled.View(({ width }: { width: number }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  width,
  paddingLeft: SCREEN_PADDING,
}));

const ScrollContainer = styled(ScrollView)({
  flexDirection: 'row',
});

export default FilterPills;
